import React from 'react';
import { X, Package, User, CreditCard, Calendar, MapPin } from 'lucide-react';

const OrderDetailModal = ({ order, onClose, onProcessRefund }) => {
  if (!order) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'refunded': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getRefundStatusColor = (status) => {
    switch (status) {
      case 'requested': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'approved': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Order Details</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
              {order.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Order Status</p>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold inline-block ${getStatusColor(order.status)}`}>
                {order.status.toUpperCase()}
              </span>
            </div>

            {order.refund_status && (
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Refund Status</p>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold inline-block ${getRefundStatusColor(order.refund_status)}`}>
                  {order.refund_status.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <User size={20} className="text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Customer Information</h3>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Name:</span>
                <p className="text-gray-900 dark:text-white font-medium">{order.user?.name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                <p className="text-gray-900 dark:text-white">{order.user?.email || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Package size={20} className="text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Order Items</h3>
            </div>
            <div className="space-y-3">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                  <div>
                    <p className="text-gray-900 dark:text-white font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900 dark:text-white font-semibold">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ₹{item.price.toLocaleString()} each
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t-2 border-gray-300 dark:border-gray-600">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900 dark:text-white">Total Amount</span>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ₹{order.total_amount?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={20} className="text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Payment Information</h3>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Payment Method:</span>
                <p className="text-gray-900 dark:text-white font-medium capitalize">
                  {order.payment_method || 'Instamojo'}
                </p>
              </div>
              {order.payment_id && (
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Payment ID:</span>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{order.payment_id}</p>
                </div>
              )}
              {order.transaction_id && (
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Transaction ID:</span>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{order.transaction_id}</p>
                </div>
              )}
            </div>
          </div>

          {/* Date Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={20} className="text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Order Timeline</h3>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Created:</span>
                <p className="text-gray-900 dark:text-white">
                  {new Date(order.created_at).toLocaleString()}
                </p>
              </div>
              {order.updated_at && (
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Last Updated:</span>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(order.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Refund Info if exists */}
          {order.refund && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 dark:text-orange-400 mb-3">Refund Request</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-orange-700 dark:text-orange-300">Reason:</span>
                  <p className="text-orange-900 dark:text-orange-100">{order.refund.reason}</p>
                </div>
                <div>
                  <span className="text-sm text-orange-700 dark:text-orange-300">Requested:</span>
                  <p className="text-orange-900 dark:text-orange-100">
                    {new Date(order.refund.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-orange-700 dark:text-orange-300">Amount:</span>
                  <p className="text-orange-900 dark:text-orange-100 font-semibold">
                    ₹{order.refund.amount?.toLocaleString()}
                  </p>
                </div>
                {order.refund.admin_notes && (
                  <div>
                    <span className="text-sm text-orange-700 dark:text-orange-300">Admin Notes:</span>
                    <p className="text-orange-900 dark:text-orange-100">{order.refund.admin_notes}</p>
                  </div>
                )}
              </div>

              {/* Process Refund Button */}
              {order.refund.status === 'pending' && onProcessRefund && (
                <div className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800">
                  <button
                    onClick={() => onProcessRefund(order.refund)}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition"
                  >
                    Process Refund (Instamojo)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;